import React, { useEffect, useState, useCallback } from 'react';
import { Timeline, Spin, Empty, Tag, Typography, Button, Select, DatePicker } from 'antd';
import {
    UserOutlined,
    RobotOutlined,
    ThunderboltOutlined,
    LoadingOutlined,
    FilterOutlined,
    ClearOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import { getContactActivity } from '../inbox-v2/resources';

const { Text } = Typography;
const { RangePicker } = DatePicker;

const ACTION_COLORS = {
    'contact.created': 'green',
    'contact.created.import': 'green',
    'contact.updated': 'blue',
    'contact.updated.import': 'blue',
    'contact.deleted': 'red',
    'contact.star_changed': 'gold',
    'contact.archive_changed': 'default',
    'contact.tag_added': 'cyan',
    'contact.tag_removed': 'cyan',
    'contact.group_added': 'purple',
    'contact.group_removed': 'purple',
    'contact.groups_synced': 'purple',
    'contact.tcpa_opted_out': 'red',
    'contact.tcpa_lifted': 'green',
    'contact.campaign_enrolled': 'orange',
    'contact.campaign_message': 'orange',
    'contact.message_sent': 'geekblue',
    'contact.enrollment_dropped': 'volcano',
};

const ACTION_LABELS = {
    'contact.created': 'Created',
    'contact.created.import': 'Imported',
    'contact.updated': 'Updated',
    'contact.updated.import': 'Updated (Import)',
    'contact.deleted': 'Deleted',
    'contact.star_changed': 'Star',
    'contact.archive_changed': 'Archive',
    'contact.tag_added': 'Tag Added',
    'contact.tag_removed': 'Tag Removed',
    'contact.group_added': 'Group Added',
    'contact.group_removed': 'Group Removed',
    'contact.groups_synced': 'Groups Synced',
    'contact.tcpa_opted_out': 'Opted Out',
    'contact.tcpa_lifted': 'Opt-in Restored',
    'contact.campaign_enrolled': 'Campaign',
    'contact.campaign_message': 'Campaign SMS',
    'contact.message_sent': 'Message Sent',
    'contact.enrollment_dropped': 'Enrollment Dropped',
};

const ACTION_FILTER_OPTIONS = [
    { label: 'All Actions', value: '' },
    { label: 'Created', value: 'contact.created' },
    { label: 'Updated', value: 'contact.updated' },
    { label: 'Deleted', value: 'contact.deleted' },
    { label: 'Star Changed', value: 'contact.star_changed' },
    { label: 'Archive Changed', value: 'contact.archive_changed' },
    { label: 'Tag Added', value: 'contact.tag_added' },
    { label: 'Tag Removed', value: 'contact.tag_removed' },
    { label: 'Group Added', value: 'contact.group_added' },
    { label: 'Group Removed', value: 'contact.group_removed' },
    { label: 'Groups Synced', value: 'contact.groups_synced' },
    { label: 'TCPA Opted Out', value: 'contact.tcpa_opted_out' },
    { label: 'TCPA Restored', value: 'contact.tcpa_lifted' },
    { label: 'Campaign Enrolled', value: 'contact.campaign_enrolled' },
    { label: 'Campaign Message', value: 'contact.campaign_message' },
    { label: 'Message Sent', value: 'contact.message_sent' },
    { label: 'Enrollment Dropped', value: 'contact.enrollment_dropped' },
];

const styles = {
    container: {
        padding: '0',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
    },
    filters: {
        display: 'flex',
        gap: 8,
        marginBottom: 12,
        flexWrap: 'wrap',
        alignItems: 'center',
        flexShrink: 0,
    },
    timelineContainer: {
        flex: 1,
        overflowY: 'auto',
        paddingTop: 12,
        paddingRight: 16,
        minHeight: 0,
    },
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 40,
        height: '100%',
    },
    activityItem: {
        paddingBottom: 4,
    },
    summary: {
        fontSize: 13,
        fontWeight: 500,
        color: '#263238',
    },
    meta: {
        fontSize: 11,
        color: '#8c8c8c',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        marginTop: 2,
    },
    details: {
        marginTop: 4,
        fontSize: 12,
        color: '#595959',
        backgroundColor: '#fafafa',
        padding: '4px 8px',
        borderRadius: 4,
        border: '1px solid #f0f0f0',
    },
    loadMore: {
        textAlign: 'center',
        marginTop: 8,
        marginBottom: 8,
    },
    header: {
        marginBottom: 12,
        fontSize: 15,
        fontWeight: 600,
        color: '#263238',
        flexShrink: 0,
    },
};

const ActorIcon = ({ actorType }) => {
    if (actorType === 'system') return <RobotOutlined style={{ fontSize: 12 }} />;
    if (actorType === 'campaign') return <ThunderboltOutlined style={{ fontSize: 12 }} />;
    return <UserOutlined style={{ fontSize: 12 }} />;
};

const renderDetails = (action, details) => {
    if (!details || Object.keys(details).length === 0) return null;

    if (action === 'contact.updated' && details.changes) {
        return (
            <div style={styles.details}>
                {Object.entries(details.changes).map(([field, change]) => (
                    <div key={field}>
                        <Text type="secondary" style={{ fontSize: 12 }}>{field}:</Text>{' '}
                        <Text delete style={{ fontSize: 12, color: '#ff4d4f' }}>{change.old || '(empty)'}</Text>
                        {' → '}
                        <Text style={{ fontSize: 12, color: '#52c41a' }}>{change.new || '(empty)'}</Text>
                    </div>
                ))}
            </div>
        );
    }

    return null;
};

const ActivityTab = ({ contactId, active }) => {
    const [activities, setActivities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [pagination, setPagination] = useState({ page: 1, total: 0, totalPages: 0 });
    const [actionFilter, setActionFilter] = useState('');
    const [dateRange, setDateRange] = useState(null);

    const fetchActivities = useCallback(async (page = 1, append = false) => {
        if (page === 1) setLoading(true);
        else setLoadingMore(true);

        try {
            const params = { page, pageSize: 25 };
            if (actionFilter) params.action = actionFilter;
            if (dateRange && dateRange[0]) params.from = dateRange[0].toISOString();
            if (dateRange && dateRange[1]) params.to = dateRange[1].toISOString();

            const res = await getContactActivity(contactId, params);
            // Response may be { data, pagination } or nested inside res.data
            const payload = res.data && res.pagination ? res : (res.data || res);
            const items = payload.data || [];
            const pag = payload.pagination || { page, pageSize: 25, total: 0, totalPages: 0 };

            setActivities(prev => append ? [...prev, ...items] : items);
            setPagination(pag);
        } catch (err) {
            console.error('Failed to fetch activity:', err);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, [contactId, actionFilter, dateRange]);

    useEffect(() => {
        if (contactId && active) {
            fetchActivities(1, false);
        }
    }, [contactId, active, actionFilter, dateRange]);

    const handleLoadMore = () => {
        fetchActivities(pagination.page + 1, true);
    };

    const handleClearFilters = () => {
        setActionFilter('');
        setDateRange(null);
    };

    const hasFilters = actionFilter || dateRange;
    const hasMore = pagination.page < pagination.totalPages;

    if (loading) {
        return (
            <div style={styles.loadingContainer}>
                <Spin size="large" />
            </div>
        );
    }

    const timelineItems = activities.map((activity) => ({
        key: activity.id,
        color: ACTION_COLORS[activity.action] || 'gray',
        children: (
            <div style={styles.activityItem}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Tag
                        color={ACTION_COLORS[activity.action] || 'default'}
                        style={{ fontSize: 11, padding: '0 4px', lineHeight: '18px', margin: 0 }}
                    >
                        {ACTION_LABELS[activity.action] || activity.action}
                    </Tag>
                </div>
                <div style={styles.summary}>{activity.summary}</div>
                <div style={styles.meta}>
                    <ActorIcon actorType={activity.actor_type} />
                    <span>
                        {activity.user_name || (activity.actor_type === 'system' ? 'System' : activity.actor_type === 'campaign' ? 'Campaign' : 'Unknown')}
                    </span>
                    <span>·</span>
                    <span>{moment(activity.created_at).format('MMM D, YYYY h:mm A')}</span>
                </div>
                {renderDetails(activity.action, activity.details)}
            </div>
        ),
    }));

    return (
        <div style={styles.container}>
            <div style={styles.header}>Activity Log</div>

            <div style={styles.filters}>
                <Select
                    value={actionFilter}
                    onChange={setActionFilter}
                    options={ACTION_FILTER_OPTIONS}
                    style={{ width: 160 }}
                    size="small"
                    suffixIcon={<FilterOutlined />}
                />
                <RangePicker
                    value={dateRange}
                    onChange={setDateRange}
                    size="small"
                    style={{ width: 220 }}
                />
                {hasFilters && (
                    <Button
                        size="small"
                        icon={<ClearOutlined />}
                        onClick={handleClearFilters}
                    >
                        Clear
                    </Button>
                )}
                <Text type="secondary" style={{ fontSize: 12, marginLeft: 'auto' }}>
                    {pagination.total} {pagination.total === 1 ? 'entry' : 'entries'}
                </Text>
            </div>

            {activities.length === 0 ? (
                <Empty
                    description={hasFilters ? 'No activity matches your filters' : 'No activity recorded yet'}
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
            ) : (
                <div style={styles.timelineContainer} className="scrollableListDiv">
                    <Timeline items={timelineItems} />
                    {hasMore && (
                        <div style={styles.loadMore}>
                            <Button
                                size="small"
                                onClick={handleLoadMore}
                                loading={loadingMore}
                            >
                                Load More
                            </Button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ActivityTab;
